import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getAddressInfo, saveAddressInfo, getEditRequestStatus, } from '../../../services/candidate/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Typography } from '@mui/material';
import { FaTimes, FaFileUpload } from 'react-icons/fa';
import './address-info.css';
import 'react-toastify/dist/ReactToastify.css';
import { getS3key } from '../../../services/employee/api';

const AddressInfo = ({ isEditMode: routeEditMode = false }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    permanentAddressLine1: '',
    permanentAddressLine2: '',
    permanentCountry: 'India',
    permanentState: '',
    permanentCity: '',
    permanentPinCode: '',
    currentAddressLine1: '',
    currentAddressLine2: '',
    currentCountry: 'India',
    currentState: '',
    currentCity: '',
    currentPinCode: '',
    sameAsPermanent: false,
  });
  const [files, setFiles] = useState({
    permanentAddressFile: null,
    currentAddressFile: null,
  });
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const permFileInputRef = useRef(null);
  const currFileInputRef = useRef(null);
  const [dragActivePerm, setDragActivePerm] = useState(false);
  const [dragActiveCurr, setDragActiveCurr] = useState(false);

  const getFileNameFromUrl = (url) => {
    if (!url) return null;
    try {
      const pathname = new URL(url).pathname;
      return decodeURIComponent(pathname.split('/').pop());
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const loadAddressData = async () => {
      setIsFetching(true);
      let finalDataToSet = null;

      try {
        const fetchedData = await getAddressInfo();
        const apiData = fetchedData?.data?.data;

        if (apiData) {
          const isSame =
            apiData.permanentAddressLine1 === apiData.currentAddressLine1 &&
            apiData.permanentAddressLine2 === apiData.currentAddressLine2 &&
            apiData.permanentCountry === apiData.currentCountry &&
            apiData.permanentState === apiData.currentState &&
            apiData.permanentCity === apiData.currentCity &&
            apiData.permanentPinCode === apiData.currentPinCode &&
            (apiData.permanentAddressFile === apiData.currentAddressFile ||
              (!apiData.permanentAddressFile && !apiData.currentAddressFile));

finalDataToSet = {
  ...formData,
  ...apiData,
  currentCountry:
    apiData.currentCountry &&
    apiData.currentCountry.trim().toLowerCase() !== 'null'
      ? apiData.currentCountry
      : '',
  currentPinCode:
    apiData.currentPinCode &&
    apiData.currentPinCode.trim().toLowerCase() !== 'null'
      ? apiData.currentPinCode
      : '',
  sameAsPermanent: isSame,
};

          setFiles({
            permanentAddressFile: apiData.permanentAddressFile
              ? {
                url: apiData.permanentAddressFile,
                name: getFileNameFromUrl(apiData.permanentAddressFile),
              }
              : null,
            currentAddressFile: apiData.currentAddressFile
              ? {
                url: apiData.currentAddressFile,
                name: getFileNameFromUrl(apiData.currentAddressFile),
              }
              : null,
          });
        }
      } catch (err) {

        if (err.response?.status === 404) {
          // No data yet → expected case
          finalDataToSet = {
            ...formData,
            permanentCountry: 'India',
            currentCountry: 'India',
          };
        } else {
          setError("Failed to load address data. Please try again.");
        }
      } finally {
        if (!finalDataToSet) {
          finalDataToSet = {
            ...formData,
            permanentCountry: 'India',
            currentCountry: 'India',
          };
        }

        setFormData(finalDataToSet);
        setIsFetching(false);
      }
    };

    loadAddressData();
  }, []);

  useEffect(() => {
    if (formData.sameAsPermanent) {
      setFormData((prev) => ({
        ...prev,
        currentAddressLine1: prev.permanentAddressLine1,
        currentAddressLine2: prev.permanentAddressLine2,
        currentCountry: prev.permanentCountry,
        currentState: prev.permanentState,
        currentCity: prev.permanentCity,
        currentPinCode: prev.permanentPinCode,
      }));
      setFiles((prev) => ({
        ...prev,
        currentAddressFile: prev.permanentAddressFile,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        currentAddressLine1: '',
        currentAddressLine2: '',
        currentCountry: 'India',
        currentState: '',
        currentCity: '',
        currentPinCode: '',
      }));
      setFiles((prev) => ({
        ...prev,
        currentAddressFile: null,
      }));
    }
    }, [formData.sameAsPermanent]); 
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    let updatedValue = value;

    const textFields = [
      'permanentAddressLine1',
      'permanentAddressLine2',
      'permanentState',
      'permanentCity',
      'currentAddressLine1',
      'currentAddressLine2',
      'currentState',
      'currentCity',
    ];

    if (textFields.includes(name) && value.length > 0) {
      updatedValue = value.charAt(0).toUpperCase() + value.slice(1);
    }

    if (
      [
        'permanentCountry',
        'permanentState',
        'permanentCity',
        'currentCountry',
        'currentState',
        'currentCity',
      ].includes(name)
    ) {
      updatedValue = updatedValue
        .replace(/[^a-zA-Z\s]/g, '')
        .slice(0, 50);
    }

    if (['permanentPinCode', 'currentPinCode'].includes(name)) {
      updatedValue = value.replace(/[^0-9]/g, '').slice(0, 6);
    }

    if (type === 'checkbox') {
      updatedValue = checked;
    }

    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
  }, []);

  const handlePrevious = () => {
    navigate('/candidate/personal-info');
  };

  const handleFileChange = useCallback((e, field) => {
    const file = e.target.files[0];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file && file.type === 'application/pdf' && file.size > 0 && file.size <= maxSize) {
      setFiles((prev) => ({ ...prev, [field]: file }));
      setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    } else {
      setValidationErrors((prev) => ({ ...prev, [field]: 'Only PDF files under 5MB are allowed' }));
      if (file) {
        toast.error(file.type !== 'application/pdf' ? 'Please upload a valid PDF file.' : 'File size must be less than 5MB.');
      }
    }
  }, []);

  const handleRemoveFile = (field) => {
    setFiles((prev) => ({ ...prev, [field]: null }));
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handlePermDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActivePerm(true);
    } else if (e.type === 'dragleave') {
      setDragActivePerm(false);
    }
  }, []);

  const handlePermDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActivePerm(false);
      const droppedFile = e.dataTransfer.files[0];
      const maxSize = 5 * 1024 * 1024;
      if (droppedFile && droppedFile.type === 'application/pdf' && droppedFile.size > 0 && droppedFile.size <= maxSize) {
        setFiles((prev) => ({ ...prev, permanentAddressFile: droppedFile }));
        setValidationErrors((prev) => ({ ...prev, permanentAddressFile: '' }));
      } else {
        setValidationErrors((prev) => ({ ...prev, permanentAddressFile: 'Only PDF files under 5MB are allowed' }));
        toast.error(droppedFile ? (droppedFile.type !== 'application/pdf' ? 'Please upload a valid PDF file.' : 'File size must be less than 5MB.') : 'No valid file dropped.');
      }
    },
    []
  );

  const handleCurrDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActiveCurr(true);
    } else if (e.type === 'dragleave') {
      setDragActiveCurr(false);
    }
  }, []);

  const handleCurrDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActiveCurr(false);
      const droppedFile = e.dataTransfer.files[0];
      const maxSize = 5 * 1024 * 1024;
      if (droppedFile && droppedFile.type === 'application/pdf' && droppedFile.size > 0 && droppedFile.size <= maxSize) {
        setFiles((prev) => ({ ...prev, currentAddressFile: droppedFile }));
        setValidationErrors((prev) => ({ ...prev, currentAddressFile: '' }));
      } else {
        setValidationErrors((prev) => ({ ...prev, currentAddressFile: 'Only PDF files under 5MB are allowed' }));
        toast.error(droppedFile ? (droppedFile.type !== 'application/pdf' ? 'Please upload a valid PDF file.' : 'File size must be less than 5MB.') : 'No valid file dropped.');
      }
    },
    []
  );

  const triggerPermFileInput = useCallback(() => {
    permFileInputRef.current.click();
  }, []);

  const triggerCurrFileInput = useCallback(() => {
    currFileInputRef.current.click();
  }, []);


  const validateField = () => {
    return '';
  };

  const validate = useCallback(() => {
    const errors = {};

    if (
      !(files.permanentAddressFile instanceof File
        ? files.permanentAddressFile.size > 0
        : files.permanentAddressFile?.url)
    ) {
      errors.permanentAddressFile = 'Permanent Address Proof File is required';
    }

    // Current file validation (only if not sameAsPermanent)
    if (
      !formData.sameAsPermanent &&
      !(files.currentAddressFile instanceof File
        ? files.currentAddressFile.size > 0
        : files.currentAddressFile?.url)
    ) {
      errors.currentAddressFile = 'Current Address Proof File is required';
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error('Please upload required address proof files.');
      return false;
    }

    return true;
  }, [formData, files]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validate()) {
        return;
      }

      setIsSubmitting(true);
      setError(null);

      const payload = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (
          key !== 'permanentAddressFile' &&
          key !== 'currentAddressFile' &&
          key !== 'permanentAddressFileName' &&
          key !== 'currentAddressFileName' &&
          key !== 'permanentAddressFileFromDB' &&
          key !== 'currentAddressFileFromDB'
        ) {
          payload.append(key, value);
        }
      });

      const getFileFromEntry = async (entry, name) => {
        if (entry instanceof File) {
          return entry;
        }

        if (entry?.url) {
          let key = entry.url;

          if (key.includes(".com/")) {
            key = key.split(".com/")[1];
          }

          key = decodeURIComponent(key);

          const response = await getS3key(key);

          const blob = response.data;

          return new File(
            [blob],
            entry.name || "existing-file",
            {
              type: blob.type,
            }
          );
        }

        return null;
      };

      try {
        const permanentFile = await getFileFromEntry(
          files.permanentAddressFile,
          'permanentAddressFile'
        );

        if (permanentFile) {
          payload.append(
            'permanentAddressFile',
            permanentFile
          );
        }

        const currentFile = await getFileFromEntry(
          files.currentAddressFile,
          'currentAddressFile'
        );

        if (currentFile) {
          payload.append(
            'currentAddressFile',
            currentFile
          );
        }

        const response = await saveAddressInfo(payload);

        if (response.status !== true) {
          return;
        }

        localStorage.removeItem("address_response");
        localStorage.removeItem("aadhaarFetchedFlag");

        if (routeEditMode) {
          const editRequestResponse =
            await getEditRequestStatus();

          const requests = Array.isArray(editRequestResponse)
            ? editRequestResponse
            : [];

          const addressEditRequest = requests
            .filter(
              (request) =>
                request.requestType === "ADDRESS_DETAILS"
            )
            .sort((a, b) => {
              const dateA = a.createdAt
                ? new Date(a.createdAt).getTime()
                : 0;

              const dateB = b.createdAt
                ? new Date(b.createdAt).getTime()
                : 0;

              if (dateB !== dateA) {
                return dateB - dateA;
              }

              return (
                Number(b.id || 0) -
                Number(a.id || 0)
              );
            })[0];

          if (
            addressEditRequest?.status !== "APPROVED"
          ) {
            toast.error(
              "No approved Address edit request found."
            );
            return;
          }


          const EDIT_ORDER = [
            "PERSONAL_DETAILS",
            "ADDRESS_DETAILS",
            "EDUCATION_DETAILS",
            "EMPLOYMENT_DETAILS",
          ];

          const getLatestRequestByType = (
            requestType
          ) => {
            return requests
              .filter(
                (request) =>
                  request.requestType === requestType
              )
              .sort((a, b) => {
                const dateA = a.createdAt
                  ? new Date(a.createdAt).getTime()
                  : 0;

                const dateB = b.createdAt
                  ? new Date(b.createdAt).getTime()
                  : 0;

                if (dateB !== dateA) {
                  return dateB - dateA;
                }

                return (
                  Number(b.id || 0) -
                  Number(a.id || 0)
                );
              })[0];
          };
          const currentIndex =
            EDIT_ORDER.indexOf("ADDRESS_DETAILS");

          const nextApprovedRequest =
            EDIT_ORDER
              .slice(currentIndex + 1)
              .find((requestType) => {
                const latestRequest =
                  getLatestRequestByType(
                    requestType
                  );

                return (
                  latestRequest?.status === "APPROVED"
                );
              });

          toast.success(
            "Address information updated successfully."
          );

          if (
            nextApprovedRequest ===
            "EDUCATION_DETAILS"
          ) {
            navigate(
              "/candidate/education-info"
            );
            return;
          }

          if (
            nextApprovedRequest ===
            "EMPLOYMENT_DETAILS"
          ) {
            navigate(
              "/candidate/employment-info"
            );
            return;
          }

          navigate(
            "/candidate/declaration"
          );

          return;
        }

        navigate(
          "/candidate/education-info"
        );

      } catch (err) {
        console.error(
          'Error submitting address info:',
          err
        );

        toast.error(
          'Failed to save address information. Please try again.'
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      files,
      validate,
      navigate,
      routeEditMode
    ]
  );

  const renderUploadField = (label, fieldName, disabled = false) => (
    <div
      className={`candidate-form-group with-bottom-border drop-zone ${disabled ? 'disabled' : ''} ${fieldName === 'permanentAddressFile' ? (dragActivePerm ? 'active' : '') : (dragActiveCurr ? 'active' : '')}`}
      onDragEnter={!disabled ? (fieldName === 'permanentAddressFile' ? handlePermDrag : handleCurrDrag) : null}
      onDragLeave={!disabled ? (fieldName === 'permanentAddressFile' ? handlePermDrag : handleCurrDrag) : null}
      onDragOver={!disabled ? (fieldName === 'permanentAddressFile' ? handlePermDrag : handleCurrDrag) : null}
      onDrop={!disabled ? (fieldName === 'permanentAddressFile' ? handlePermDrop : handleCurrDrop) : null}
    >
      <p className="label-header">
        <FaFileUpload className="filed-icons" /> {label}
      </p>
      <div className="candidate-file-upload-group">
        <input
          type="file"
          id={fieldName}
          ref={fieldName === 'permanentAddressFile' ? permFileInputRef : currFileInputRef}
          onChange={(e) => !disabled && handleFileChange(e, fieldName)}
          accept="application/pdf"
          hidden
          disabled={disabled || isSubmitting}
        />
        <div
          onClick={disabled || isSubmitting ? null : (fieldName === 'permanentAddressFile' ? triggerPermFileInput : triggerCurrFileInput)}
          className={`candidate-file-upload-button ${disabled || isSubmitting ? 'disabled' : ''}`}
        >
          Drag & Drop or Click to Upload PDF
        </div>
        {files[fieldName] && (
          <div className="file-list">
            <span className="file-list-item">
              {files[fieldName] instanceof File ? files[fieldName].name : files[fieldName].name || 'Existing File'}
              <FaTimes
                className="remove-file-icon"
                onClick={() => !disabled && handleRemoveFile(fieldName)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                    handleRemoveFile(fieldName);
                  }
                }}
                aria-label={`Remove ${files[fieldName] instanceof File ? files[fieldName].name : files[fieldName].name || 'Existing File'}`}
              />
            </span>
          </div>
        )}
        {!files[fieldName] && <span className="candidate-file-name">No file chosen</span>}
        <p className="note">* File must be less than 5MB and PDF only.</p>
      </div>
      <div className="upload-error-container">
        {validationErrors[fieldName] && (
          <Typography color="error" variant="caption" id={`${fieldName}-description`}>
            {validationErrors[fieldName]}
          </Typography>
        )}
      </div>
    </div>
  );

  const renderInputField = (label, name, type = 'text', placeholder = '', disabled = false, required = false, readOnly = false) => (
    <div className="candidate-form-group">
      <label>{label}{required ? '*' : ''}</label>
      {type === 'select' ? (
        <select
          name={name}
          value={formData[name] || ''}
          onChange={handleChange}
          //required={required}
          disabled={disabled || isSubmitting}
        >
           <option value="">Select Country</option>
          <option value="India">India</option>
          {/* Add more options as needed */}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name] || ''}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          disabled={disabled || isSubmitting}
          readOnly={readOnly}
          className={validationErrors[name] ? 'input-error' : ''}
        />
      )}
      {validationErrors[name] && (
        <Typography color="error" variant="caption" id={`${name}-error`}>
          {validationErrors[name]}
        </Typography>
      )}
    </div>
  );

  if (isFetching) {
    return (
      <div className="centered-message">
        <div className="spinner"></div>
        <div>Loading address Details ...</div>
      </div>
    );
  }

  return (
    <div className="candidate-address-page">
      <div className="candidate-container">
        {isSubmitting && (
          <div className="saving-overlay">
            <p>Saving...</p>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {/* Permanent Address Section */}
          <div className="candidate-form-section">
            <div className="address-heading-with-line">
              <div className="line"></div>
              <h3>Permanent Address Details</h3>
            </div>

            <div className="candidate-form-row">
              {renderInputField('Address Line 1', 'permanentAddressLine1', 'text', 'Address Line 1*', false, false, true)}
              {renderInputField('Address Line 2', 'permanentAddressLine2', 'text', 'Address Line 2*', false, false, true)}
            </div>

            <div className="candidate-form-row">
              {renderInputField('Country', 'permanentCountry', 'select', '', false, true, true)}
              {renderInputField('State/Region/Province', 'permanentState', 'text', 'State/Region/Province*', false, true, true)}
            </div>

            <div className="candidate-form-row">
              {renderInputField('City', 'permanentCity', 'text', 'City*', false, true, true)}
              {renderInputField('Pincode', 'permanentPinCode', 'text', 'Pincode*', false, true, true)}
            </div>

            {renderUploadField('Permanent Address Proof File* (PDF Only)', 'permanentAddressFile')}
          </div>

          <div className="candidate-checkbox-container">
            <input
              type="checkbox"
              id="sameAsPermanent"
              name="sameAsPermanent"
              checked={formData.sameAsPermanent}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <label htmlFor="sameAsPermanent">
              If your Current Address is the same as Permanent Address.
            </label>
          </div>

          {/* Current Address Section */}
          <div className="candidate-form-section candidate-current-address">
            <div className="address-heading-with-line">
              <div className="line"></div>
              <h3>Current Address Details</h3>
            </div>

            <div className="candidate-form-row">
              {renderInputField('Address Line 1', 'currentAddressLine1', 'text', 'Address Line 1*', formData.sameAsPermanent, false)}
              {renderInputField('Address Line 2', 'currentAddressLine2', 'text', 'Address Line 2*', formData.sameAsPermanent, false)}
            </div>

            <div className="candidate-form-row">
              {renderInputField('Country', 'currentCountry', 'select', '', formData.sameAsPermanent, false)}
              {renderInputField('State/Region/Province', 'currentState', 'text', 'State/Region/Province*', false)}
            </div>

            <div className="candidate-form-row">
              {renderInputField('City', 'currentCity', 'text', 'City*', formData.sameAsPermanent, false)}
              {renderInputField('Pincode', 'currentPinCode', 'text', 'Pincode*', formData.sameAsPermanent, false)}
            </div>

            {renderUploadField('Current Address Proof File* (PDF Only)', 'currentAddressFile', formData.sameAsPermanent, false)}
          </div>

          {error && <p className="error-message">{error}</p>}
          <div className="candidate-bottom-nav">
            <button type="button" className="previous" disabled={isSubmitting} onClick={handlePrevious}>Previous</button>
            <button type="submit" className="save-continue" disabled={isSubmitting}>
              Save & Continue
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressInfo;